import React, { useState } from 'react';
import Box from '@mui/material/Box';
import { Button, Chip, Paper, Stack, Typography } from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useActiveVehicle } from 'containers/pages/CANpark/context/ActiveVehicleProvider';
import TicketRecord from '../../../ticketRecord/TicketRecord';

// Collapse long histories: show the most recent N records by default, with a
// toggle to reveal the rest. `tickets` is newest-first, so the first N keep the
// in-window (recent) records visible and truncate only the oldest overflow.
const RECORD_DISPLAY_LIMIT = 10;

const ViolationRecordCard: React.FC = () => {
	const { tickets, inWindowTickets, currentSearch } = useActiveVehicle();
	const [showAll, setShowAll] = useState(false);
	const parkingLocation = currentSearch?.parkingLocation;
	if (!parkingLocation) return null;

	// Chip shows the count that *counts* (in-window). The list shows the full
	// history; out-of-window tickets render greyed (info only).
	const count = inWindowTickets.length;
	const earlierCount = tickets.length - inWindowTickets.length;

	const hasMore = tickets.length > RECORD_DISPLAY_LIMIT;
	const visibleTickets = showAll
		? tickets
		: tickets.slice(0, RECORD_DISPLAY_LIMIT);

	return (
		<Paper
			variant="outlined"
			sx={{
				display: 'flex',
				flexDirection: 'column',
				borderRadius: 2,
				p: 1.5,
				width: '100%',
			}}
		>
			<Stack
				direction="row"
				alignItems="center"
				spacing={1}
				sx={{ mb: 1.25 }}
			>
				<HistoryIcon fontSize="small" />
				<Typography variant="caption" fontWeight={300}>
					Previous Records
				</Typography>
				<Chip
					label={count}
					size="small"
					color={count > 0 ? 'primary' : 'default'}
					variant={count > 0 ? 'filled' : 'outlined'}
				/>
				{earlierCount > 0 && (
					<Chip
						label={`+${earlierCount} earlier`}
						size="small"
						color="default"
						variant="outlined"
					/>
				)}
			</Stack>

			{tickets.length === 0 ? (
				<Typography
					variant="body2"
					color="text.secondary"
					fontWeight={300}
					sx={{ py: 1 }}
				>
					No prior records at this location.
				</Typography>
			) : (
				<Box
					sx={{
						display: 'flex',
						flexDirection: 'column',
						gap: 1,
						width: '100%',
					}}
				>
					{visibleTickets.map((ticket) => (
						<TicketRecord
							key={ticket.ticketNumber}
							ticket={ticket}
							parkingLocation={parkingLocation}
						/>
					))}
					{hasMore && (
						<Button
							size="small"
							variant="text"
							onClick={() => setShowAll((v) => !v)}
							startIcon={
								showAll ? (
									<ExpandLessIcon />
								) : (
									<ExpandMoreIcon />
								)
							}
							sx={{
								alignSelf: 'flex-start',
								fontWeight: 300,
								textTransform: 'none',
								mt: 0.5,
							}}
						>
							{showAll
								? 'Show fewer'
								: `Show all ${tickets.length} records`}
						</Button>
					)}
				</Box>
			)}
		</Paper>
	);
};

export default ViolationRecordCard;
